'use client';

import { ReactElement } from 'react';
import { AbsoluteFill } from 'remotion';

// Components
import { PlusSignsComponent } from './components/PlusSignsComponent';
import { DecorativeLines } from './components/DecorativeLines';
import { DotGrid } from './components/DotGrid';

// Constants
import {
  DEFAULT_ANIMATION_CONFIG,
  DEFAULT_BACKGROUND_COLOR,
  DEFAULT_GRID_CONFIG,
  DEFAULT_HIDDEN_REGIONS,
  DEFAULT_LINE_COLOR,
  DEFAULT_LINES,
  DEFAULT_PLUS_COLOR,
  DEFAULT_PLUS_SIGNS,
} from './constants';

//Types
import {
  SnakeAnimationConfig,
  DotGridBackgroundProps,
  GridConfig,
} from './types';

/**
 * DotGridBackground - A reusable animated background component
 *
 * Features:
 * - Configurable dot grid pattern
 * - Animated decorative lines (snake crawling effect)
 * - Plus sign decorations
 * - Hidden regions to exclude dots
 */
export const DotGridBackground = ({
  backgroundColor = DEFAULT_BACKGROUND_COLOR,
  grid: gridOverrides,
  lines = DEFAULT_LINES,
  lineColor = DEFAULT_LINE_COLOR,
  plusSigns = DEFAULT_PLUS_SIGNS,
  defaultPlusColor = DEFAULT_PLUS_COLOR,
  hiddenRegions = DEFAULT_HIDDEN_REGIONS,
  animation: animationOverrides,
  showLines = true,
  showPlusSigns = true,
  width = 1920,
  height = 1080,
}: DotGridBackgroundProps): ReactElement => {
  const grid: GridConfig = {
    ...DEFAULT_GRID_CONFIG,
    ...gridOverrides,
  };

  const animation: Required<SnakeAnimationConfig> = {
    ...DEFAULT_ANIMATION_CONFIG,
    ...animationOverrides,
  };

  return (
    <AbsoluteFill style={{ backgroundColor }}>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        style={{ position: 'absolute', top: 0, left: 0 }}
      >
        <DotGrid
          grid={grid}
          hiddenRegions={hiddenRegions}
          width={width}
          height={height}
        />
        {showLines && (
          <DecorativeLines
            lines={lines}
            lineColor={lineColor}
            grid={grid}
            animation={animation}
            width={width}
            height={height}
          />
        )}
        {showPlusSigns && (
          <PlusSignsComponent
            plusSigns={plusSigns}
            defaultColor={defaultPlusColor}
            grid={grid}
            width={width}
            height={height}
          />
        )}
      </svg>
    </AbsoluteFill>
  );
};
